import React, { FC, useEffect, useMemo, useState } from "react";
import { POSAMode } from "./model";
import NiceModal, { NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import KC_Modal_2 from "../../../../designsystem/KC_Modal/KC_Modal_2";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import { LogoConfigurator } from "../../../../components/SaasGameConfiguration/LogoConfigurator";
import ColorsSelector from "../../../../components/SaasGameConfiguration/components/ColorsSelector";
import { tt } from "../../../../utils/i18n/i18n";
import SVGPreview from "../../../../components/SVGPreview/SVGPreview";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { updateCustomerThunk } from "../../../../redux/actions/user";
import { setError, setLoading } from "../../../../redux/actions/app";
import { useDispatch } from "react-redux";
import { useJetpackConfigSelector } from "../../../../redux/jetpackConfig";
import POSAApi from "../../../12_POSAManager/api";
import { arrayBufferToBase64 } from "../../../../utils/helpers/arrayBufferToBase64";
import { setCustomPOSAs } from "../../../../redux/customPOSA";
import Shimmer from "../../../../components/Shimmer/Shimmer";
import { Colors } from "kadow-jetpack-model";
import TSUtils from "../../../../utils/helpers/TSUtils";
import "./posa-editor-modal.scss";
import {
  DEFAULT_GAME_COLORS,
  DEFAULT_POSA_COLORS
} from "../../../../utils/constants";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../../designsystem/KC_Button/KC_Button";
import {
  POSA_WORDINGS,
  POSATypeJavascriptDTO
} from "@tootsweet/model/lottery/POSAType";
import { CustomPOSA_Javascript_DTO } from "@tootsweet/model/entities/saas/CustomPOSA";
import { svgRenderer, exportSVGToPNG } from "../../../../utils/svgRenderer";
import { TranslationsKeys } from "../../../../utils/i18n/translations/Translations";

interface Props {
  posa: POSATypeJavascriptDTO;
  title: string;
  posaDestination: POSAMode;
  eventId: string;
  enableDownload: boolean;
  svgID?: string;
  titles: string;
  customPOSA?: CustomPOSA_Javascript_DTO;
}

const POSAEditorModal: FC<Props & NiceModalHocProps> = NiceModal.create<Props>(
  (props) => {
    const dispatch = useDispatch();
    const modal = useModal();
    const jetpackConfig = useJetpackConfigSelector();
    const { accountToDisplay } = useAccountToDisplaySelector();

    const [colors, setColors] = useState<Colors>(DEFAULT_POSA_COLORS);
    const [logoZoom, setLogoZoom] = useState<number>(1);
    const [selectedWording, setSelectedWording] = useState<string>(
      POSA_WORDINGS.gift
    );

    const isColorEditable = useMemo(() => {
      return accountToDisplay?.saasFeatures?.t;
    }, [accountToDisplay]);

    const handleClose = () => {
      modal.resolve();
      void modal.hide();
    };

    const handleSave = async () => {
      if (!accountToDisplay?.token || !accountToDisplay?.trackingID) {
        return;
      }

      dispatch(setLoading(true));

      try {
        await TSUtils.sleep(1000);

        const svgElementId = svgRenderer.generateSVGId(props.posa._id);
        const pngData = await exportSVGToPNG.convertSVGToPng(
          svgElementId,
          props.posa
        );

        if (!pngData) {
          alert(tt("error"));
          return;
        }

        const base64String = arrayBufferToBase64(pngData);

        const uploadResult = await POSAApi.uploadPOSAPng(
          accountToDisplay.trackingID,
          base64String,
          props.posa,
          colors,
          accountToDisplay?.token
        );

        dispatch(setCustomPOSAs(uploadResult.customPOSAs));
      } catch (error) {
        console.error("Error saving POSA:", error);
        dispatch(setError(tt("api_update_color_error")));
      } finally {
        modal.resolve();
        void modal.hide();
        dispatch(setLoading(false));
      }
    };

    const onPOSAColorChanged = async (key: string, hexColor: string) => {
      if (!key || !hexColor) {
        return;
      }

      try {
        const newPlvColors = {
          ...colors,
          [key]: hexColor
        };

        setColors(newPlvColors);
        dispatch(
          updateCustomerThunk(
            { plvColors: newPlvColors },
            accountToDisplay?.trackingID,
            accountToDisplay?.token
          )
        );
      } catch (e) {
        dispatch(setError(tt("api_update_color_error")));
      }
    };

    function renderLogoSlider() {
      if (!props.posa?.isCustom) {
        return null;
      }
      return (
        <>
          {!!jetpackConfig?.logo_url && (
            <div className="w-100 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-center justify-content-sm-between border-top py-4">
              <KC_Heading3 text={tt("logo_size")} className="mr-4" />
              <div className="d-flex flex-row align-items-center mt-2 mt-sm-0 logo-size-slider-container">
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={logoZoom}
                  onChange={(e) => setLogoZoom(parseFloat(e.target.value))}
                  className="logo-size-slider"
                />
                <span className="ml-2">{logoZoom.toFixed(1)}x</span>
              </div>
            </div>
          )}
        </>
      );
    }

    useEffect(() => {
      if (modal.visible) {
        setLogoZoom(1);
      }
    }, [modal.visible]);

    useEffect(() => {
      if (!modal.visible) {
        return;
      }

      if (accountToDisplay?.plvColors) {
        setColors(accountToDisplay.plvColors);
      } else if (props.customPOSA?.colors) {
        setColors(props.customPOSA.colors);
      } else if (jetpackConfig?.colors) {
        const isDefault =
          jetpackConfig.colors.background?.toLowerCase() ===
            DEFAULT_GAME_COLORS.background?.toLowerCase() &&
          jetpackConfig.colors.titles?.toLowerCase() ===
            DEFAULT_GAME_COLORS.titles?.toLowerCase() &&
          jetpackConfig.colors.accent?.toLowerCase() ===
            DEFAULT_GAME_COLORS.accent?.toLowerCase();

        if (isDefault) {
          return;
        }

        setColors({
          background:
            jetpackConfig.colors?.background || DEFAULT_POSA_COLORS.background,
          primary: jetpackConfig.colors?.titles || DEFAULT_POSA_COLORS.primary,
          accent: jetpackConfig.colors?.accent || DEFAULT_POSA_COLORS.accent
        });
      }
    }, [props.customPOSA, accountToDisplay, jetpackConfig, modal.visible]);

    function renderLogoSection() {
      if (!props.posa?.isCustom) {
        return null;
      }
      return (
        <div
          className="w-100 d-flex justify-content-between mb-3"
          style={{ height: 120 }}
        >
          <div className="d-flex justify-content-start align-items-center">
            <KC_Heading3 text="Logo" />
          </div>
          <div
            className="d-flex align-items-center justify-content-end"
            style={{ gap: 12 }}
          >
            <LogoConfigurator isPOSA={true} onChanged={onPOSAColorChanged} />
          </div>
        </div>
      );
    }

    return (
      <KC_Modal_2
        id="posa-editor-modal"
        isOpen={modal.visible}
        onClose={handleClose}
        header={
          <div className="px-4 py-3">
            <KC_Heading3 text={props.title} />
          </div>
        }
      >
        <div className="d-flex flex-column px-4 pb-4">
          <div className="d-flex flex-column flex-sm-row">
            <div className="pr-0 pr-sm-3 flex-grow-1 d-flex flex-column justify-content-end">
              {renderLogoSection()}
              {renderLogoSlider()}
              {isColorEditable && !!colors && !!props.posa?.isCustom && (
                <div>
                  <ColorsSelector
                    isPOSA={true}
                    onColorChanged={onPOSAColorChanged}
                    primary={colors.primary}
                    accent={colors.accent}
                    background={colors.background}
                    subtitle={colors.subtitle}
                    posa={props.posa}
                  />
                </div>
              )}
              {!props.posa?.flags?.hideWordings && (
                <div className="d-flex align-items-center mt-3">
                  <label className="flex-grow-1">
                    <KC_Heading3 text={tt("posa_wording_label")} />
                  </label>
                  <div className="kc-input-wrapper" style={{ maxWidth: 330 }}>
                    <select
                      className="kc_input"
                      value={selectedWording}
                      onChange={(e) => setSelectedWording(e.target.value)}
                    >
                      {Object.values(POSA_WORDINGS).map((wording) => (
                        <option key={wording} value={wording}>
                          {tt(POSA_WORDING_TRANSLATION_MAP[wording])}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <KC_Button
                id="posa-editor-save-btn"
                isPrimary={true}
                type={KC_ButtonType.Button}
                size={KC_ButtonSize.Large}
                onClick={handleSave}
                text={tt("save")}
                extraStyle={{
                  pointerEvents: "none",
                  opacity: 0
                }}
                className="d-flex mt-4 ml-auto pr-4 pl-4 choose-activity-btn"
              />
            </div>
            <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1">
              {!!jetpackConfig?.configId && (
                <div className="mt-4 mt-md-auto mb-md-auto">
                  <SVGPreview
                    posa={props.posa}
                    eventId={props.eventId}
                    posaDestination={props.posaDestination}
                    isEditing={true}
                    existingSvgId={props.svgID}
                    primary={colors.primary || ""}
                    accent={colors.accent || ""}
                    background={colors.background || ""}
                    subtitle={colors.subtitle || ""}
                    configId={jetpackConfig.configId}
                    logo_url={jetpackConfig.logo_url}
                    logoZoom={logoZoom}
                    pegLogoX={!!props.posa.flags?.pegLogoX}
                    onFinishedRender={() => {
                      if (props.posa.isCustom) {
                        return;
                      }
                      // void handleSave();
                    }}
                    wording={selectedWording}
                  />
                </div>
              )}

              {!props.enableDownload && jetpackConfig?.configId && (
                <KC_Button
                  id="posa-editor-save-btn"
                  isPrimary={true}
                  type={KC_ButtonType.Button}
                  size={KC_ButtonSize.Large}
                  onClick={handleSave}
                  text={tt("save")}
                  className="d-flex mt-4 ml-auto pr-4 pl-4 choose-activity-btn"
                />
              )}

              {!jetpackConfig?.configId && <Shimmer />}
            </div>
          </div>
        </div>
      </KC_Modal_2>
    );
  }
);

export default POSAEditorModal;

const POSA_WORDING_TRANSLATION_MAP: Record<
  POSA_WORDINGS,
  keyof TranslationsKeys
> = {
  [POSA_WORDINGS.gift]: "posa_wording_cadeau",
  [POSA_WORDINGS.iphone]: "posa_wording_iphone",
  [POSA_WORDINGS.stay]: "posa_wording_sejour",
  [POSA_WORDINGS.dish]: "posa_wording_plat",
  [POSA_WORDINGS.dish_drink]: "posa_wording_plats_boissons",
  [POSA_WORDINGS.cocktail]: "posa_wording_cocktail",
  [POSA_WORDINGS.pizza]: "posa_wording_pizza",
  [POSA_WORDINGS.burger]: "posa_wording_burger",
  [POSA_WORDINGS.meal]: "posa_wording_repas"
};
